import React from "react";
import { ProjectDraft, ProjectOutline, Workstream } from "tekkr-common/dist/model/project/project";
import { useBlocker, useNavigate, useSearchParams } from "react-router-dom";
import { ReplaceAllIcon, RocketIcon } from "lucide-react";
import { CreateProjectChatPageApi } from "../create-project-chat-page";
import { useCreateProjectMutation } from "../../../data/mutations/create-project";
import { useReviseProjectMutation } from "../../../data/mutations/revise-project";
import { cn } from "../../../lib/utils";
import { ProjectOutlineView } from "../../../components/shared/project/project-outline-view";
import { AiButton } from "../../../components/shared/ai-button";
import { Button } from "../../../components/ui/button";
import ConfirmationDialog from "../../../components/modals/confirmation-dialog";
import { Dialog, DialogContent, DialogFooter, DialogTitle, DialogTrigger } from "../../../components/ui/dialog";
import { useGetProjectQuery } from "../../../data/queries/get-project";
import { useFlags } from "flagsmith/react";
import { useKickOffProjectMutation } from "../../../data/mutations/kick-off-project";
import { useCloseConfirmation } from "../../../components/hooks/close-confirmation";

interface Props {
    page: CreateProjectChatPageApi;
    message: string;
    interactionsEnabled: boolean;
}

export function DraftPlanPreview(props: Props & { draft: ProjectDraft }) {
    const hasUnsavedChanges = !!props.page.reviseProjectId;
    useCloseConfirmation(hasUnsavedChanges);
    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) => hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
    );

    return (
        <>
            <PreviewView {...props} />
            <UnsavedChangesDialog
                open={blocker.state === "blocked"}
                onKeepEditing={() => blocker.reset?.()}
                onDiscardChanges={() => blocker.proceed?.()}
            />
        </>
    );
}

export function ReviseDraftPlanPreview(props: Props & { projectId: string }) {
    const res = useGetProjectQuery(props.projectId);

    if (!res.data) {
        return <>this is still loading</>;
    }

    const draft: ProjectDraft = {
        title: res.data.name,
        objectives: res.data.objectives,
        workstreams: res.data.outline.workstreams,
    };
    return <PreviewView {...props} draft={draft} />;
}

function PreviewView({ page, draft, ...props }: Props & { draft: ProjectDraft }) {
    const navigate = useNavigate();
    const flags = useFlags(["skip-project-alignment"]);
    const skipAlignment = flags["skip-project-alignment"]?.enabled;

    const launchProjectMutation = useCreateProjectMutation(page.chatId);
    const kickOffMutation = useKickOffProjectMutation();

    const handleProjectLaunch = async () => {
        const result = await launchProjectMutation.mutateAsync();
        if (skipAlignment) {
            await kickOffMutation.mutateAsync({ projectId: result.projectId });
            navigate(`/project/${result.projectId}`, { replace: true });
        } else {
            navigate(`/project/${result.projectId}/plan-alignment`, { replace: true });
        }
    };

    const [searchParams] = useSearchParams();
    const reviseProjectMutation = useReviseProjectMutation(page.chatId, {
        onSuccess: ({ projectId }) => {
            if (searchParams.get("revise-source") === "plan-alignment") {
                navigate(`/project/${projectId}/plan-alignment`, { replace: true });
            } else {
                navigate(`/project/${projectId}`, { replace: true });
            }
        },
    });
    async function reviseProject() {
        await reviseProjectMutation.mutateAsync({
            projectId: page.reviseProjectId!,
        });
    }

    const readiness = {
        workstreams: hasWorkstreams(draft),
        objectives: hasObjectives(draft),
        title: hasTitle(draft),
    };
    const planReady = readiness.workstreams && readiness.objectives && readiness.title;

    return (
        <div
            className={
                "create-project-chat-column-width fixed top-[48pt] max-h-[calc(100vh-48pt-48pt)] overflow-y-scroll px-4 pt-[5vh]"
            }
        >
            <div>
                <div className={"text-sm text-muted-foreground"}>Project</div>
                <h3 className={cn(!draft.title && "animate-pulse-light select-none blur-md delay-500")}>
                    {draft.title ?? "Lorem Ipsum dolor Sit Amet"}
                </h3>
            </div>
            <div className={"my-4 font-semibold"}>Objectives</div>
            <div className={"grid grid-cols-[16pt_auto] gap-y-2 text-sm font-medium"}>
                {hasObjectives(draft)
                    ? (draft.objectives as string[]).map((o, i) => (
                          <React.Fragment key={i}>
                              <div className={"w-4"}>-&gt;</div>
                              <div>{o}</div>
                          </React.Fragment>
                      ))
                    : fakeObjectives.map((o, i) => (
                          <React.Fragment key={i}>
                              <div className={"w-4"}>-&gt;</div>
                              <div
                                  className={`animate-pulse-light select-none blur-sm`}
                                  style={{ animationDelay: `${i * 350}ms` }}
                              >
                                  {o}
                              </div>
                          </React.Fragment>
                      ))}
            </div>
            <div className={"my-4 font-semibold"}>Workstreams</div>
            {hasWorkstreams(draft) ? (
                <ProjectOutlineView
                    plan={{ workstreams: draft.workstreams }}
                    chatId={page.chatId}
                    reviseProjectId={page.reviseProjectId ?? undefined}
                />
            ) : (
                <div className={"animate-pulse-light pointer-events-none select-none blur-sm"}>
                    <ProjectOutlineView plan={fakePlan} />
                </div>
            )}
            {props.interactionsEnabled && (
                <div className={"sticky bottom-0 mt-6 flex flex-col bg-background"}>
                    <div className={"h-0 w-full overflow-visible"}>
                        <div
                            className={"absolute -top-6 h-6 w-full bg-gradient-to-b from-transparent to-background"}
                        ></div>
                    </div>
                    <div className={"flex flex-row items-center gap-3 bg-background"}>
                        {!page.reviseProjectId ? (
                            <AiButton
                                disabled={!planReady}
                                isLoading={launchProjectMutation.isPending || kickOffMutation.isPending}
                                onClick={handleProjectLaunch}
                            >
                                <RocketIcon className={"me-2 h-5 w-5"} />
                                Continue with this plan
                            </AiButton>
                        ) : (
                            <ConfirmRevisionButton
                                disabled={!planReady || reviseProjectMutation.isPending}
                                onRevise={reviseProject}
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function ConfirmRevisionButton(props: { onRevise: () => Promise<void>; disabled?: boolean }) {
    const dialogBody = (
        <div>
            <p className={"font-medium"}>Are you sure you want to apply these changes?</p>
            <p className={"text-sm text-muted-foreground"}>
                These changes will restructure the project and might result in some information being lost if it's no
                longer relevant to the revised draft.
            </p>
        </div>
    );
    return (
        <ConfirmationDialog title={"Update Project Plan"} body={dialogBody} confirm={props.onRevise}>
            <DialogTrigger asChild>
                <Button disabled={props.disabled}>
                    <ReplaceAllIcon className={"me-2 h-5 w-5"} />
                    Update project plan
                </Button>
            </DialogTrigger>
        </ConfirmationDialog>
    );
}

function hasWorkstreams(draft: ProjectDraft): draft is ProjectDraft & { workstreams: Workstream[] } {
    return (draft.workstreams?.length ?? 0) > 0;
}

function hasTitle(draft: ProjectDraft): draft is ProjectDraft & { title: string } {
    return !!draft.title && draft.title.trim().length > 0;
}

function hasObjectives(draft: ProjectDraft): draft is ProjectDraft & { objectives: string[] } {
    return (draft.objectives?.length ?? 0) > 0;
}

export function isDraftReadyToShowPreview(draft: ProjectDraft): boolean {
    return hasWorkstreams(draft) || hasObjectives(draft) || hasTitle(draft);
}

const fakeObjectives = [
    "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
    "Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
    "Ut enim ad minim veniam",
];
const fakePlan: ProjectOutline = {
    workstreams: [
        {
            deliverables: [],
            description: "Some description of the workstream",
            id: "A",
            name: "Lorem ipsum dolor sit amet",
        },
        {
            deliverables: [],
            description: "Some description of the workstream",
            id: "B",
            name: "Dolor sit amet, consectetur",
        },
        {
            deliverables: [],
            description: "Some description of the workstream",
            id: "C",
            name: "Sit amet, consectetur, adipiscing",
        },
    ],
};

function UnsavedChangesDialog(props: { open: boolean; onKeepEditing: () => void; onDiscardChanges: () => void }) {
    return (
        <Dialog open={props.open} onOpenChange={(open) => !open && props.onKeepEditing()}>
            <DialogContent>
                <DialogTitle>You have unsaved changes</DialogTitle>
                <div className="pb-8 pt-2">
                    <p className="text-md text-muted-foreground">
                        Your project changes aren't saved yet. Click "Update Project Plan" below to save them.
                    </p>
                </div>
                <DialogFooter>
                    <Button onClick={props.onKeepEditing} variant="secondary">
                        Keep editing
                    </Button>
                    <Button onClick={props.onDiscardChanges} variant="destructive">
                        Discard changes
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
